import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { UploadCloud, X, Loader2, Check } from "lucide-react";
import { Reveal } from "./Motion";
import { api } from "../lib/api";

const CHANNELS = [
  { v: "text", l: "Text me" },
  { v: "call", l: "Call me" },
  { v: "email", l: "Email me" },
];

export default function BidForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", channel: "text", address: "", description: "", out_of_town: false });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onDrop = useCallback(async (accepted) => {
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of accepted.slice(0, 6)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ path: data.path, name: f.name, preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null });
      }
      setFiles((prev) => [...prev, ...uploaded]);
    } catch {
      toast.error("Upload failed. Try a smaller file.");
    } finally {
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { "image/*": [], "application/pdf": [] }, maxSize: 15 * 1024 * 1024 });

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || (!form.phone && !form.email)) {
      toast.error("Add your name and a phone or email so we can send your bid.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, file_paths: files.map((f) => f.path) });
      setDone(true);
      toast.success("Request sent! We'll get you a bid shortly.");
    } catch {
      toast.error("Something went wrong. Call us and we'll sort it out.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="bid" data-testid="bid-section" className="relative bg-[#050505] py-24 md:py-36 border-t border-[var(--border-dim)]">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--accent)]">/ Instant Bid</span>
            <h2 className="font-display text-5xl md:text-7xl font-black uppercase tracking-tighter mt-4 leading-[0.88]">Snap it.<br />Send it.<br /><span className="text-[var(--accent)]">Get a bid.</span></h2>
            <p className="text-[var(--text-secondary)] leading-relaxed mt-8 max-w-md">Add a couple photos of your junk, tell us where it is, and pick how you want to hear back. Most bids go out same day.</p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          {done ? (
            <div data-testid="bid-success" className="border border-[var(--accent)] bg-[#0a0a0a] p-10 flex flex-col items-start gap-4 h-full justify-center">
              <div className="h-14 w-14 bg-[var(--accent)] flex items-center justify-center"><Check size={28} /></div>
              <h3 className="font-display text-3xl font-bold uppercase">Request received</h3>
              <p className="text-[var(--text-secondary)]">Thanks, {form.name.split(" ")[0]}. We've logged your request and will reach out by {form.channel}. Keep your phone close.</p>
            </div>
          ) : (
          <form onSubmit={submit} className="border border-[var(--border-dim)] bg-[#0a0a0a] p-6 md:p-8 space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <input data-testid="bid-name" value={form.name} onChange={set("name")} placeholder="Your name *" className="bg-[#050505] border border-[var(--border-dim)] px-4 py-3.5 font-mono text-sm focus:border-[var(--accent)] focus:outline-none placeholder:text-[var(--text-secondary)]" />
              <input data-testid="bid-phone" value={form.phone} onChange={set("phone")} placeholder="Phone" className="bg-[#050505] border border-[var(--border-dim)] px-4 py-3.5 font-mono text-sm focus:border-[var(--accent)] focus:outline-none placeholder:text-[var(--text-secondary)]" />
            </div>
            <input data-testid="bid-email" value={form.email} onChange={set("email")} placeholder="Email" className="w-full bg-[#050505] border border-[var(--border-dim)] px-4 py-3.5 font-mono text-sm focus:border-[var(--accent)] focus:outline-none placeholder:text-[var(--text-secondary)]" />
            <input data-testid="bid-address" value={form.address} onChange={set("address")} placeholder="Pickup address or area" className="w-full bg-[#050505] border border-[var(--border-dim)] px-4 py-3.5 font-mono text-sm focus:border-[var(--accent)] focus:outline-none placeholder:text-[var(--text-secondary)]" />
            <textarea data-testid="bid-description" value={form.description} onChange={set("description")} placeholder="What needs to go? (a couch, garage full of boxes, old fridge...)" rows={3} className="w-full bg-[#050505] border border-[var(--border-dim)] px-4 py-3.5 font-mono text-sm focus:border-[var(--accent)] focus:outline-none placeholder:text-[var(--text-secondary)] resize-none" />

            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-3">How should we reach you?</p>
              <div className="flex gap-2">
                {CHANNELS.map((c) => (
                  <button key={c.v} type="button" data-testid={`bid-channel-${c.v}`} onClick={() => setForm({ ...form, channel: c.v })} className={`flex-1 py-3 font-mono text-xs uppercase tracking-widest border transition-colors ${form.channel === c.v ? "bg-[var(--accent)] border-[var(--accent)] text-white" : "border-[var(--border-dim)] text-[var(--text-secondary)] hover:border-white"}`}>{c.l}</button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-3 cursor-pointer" data-testid="bid-oot-toggle">
              <input type="checkbox" checked={form.out_of_town} onChange={(e) => setForm({ ...form, out_of_town: e.target.checked })} className="accent-[var(--accent)] h-4 w-4" />
              <span className="font-mono text-xs uppercase tracking-widest text-[var(--text-secondary)]">This is an out-of-town / long-distance haul</span>
            </label>

            {/* dropzone */}
            <div {...getRootProps()} data-testid="bid-dropzone" className={`border border-dashed p-6 text-center cursor-pointer transition-colors ${isDragActive ? "border-[var(--accent)] bg-[var(--accent)]/5" : "border-[var(--border-dim)] hover:border-white"}`}>
              <input {...getInputProps()} data-testid="bid-file-input" />
              {uploading ? <Loader2 className="mx-auto animate-spin text-[var(--accent)]" /> : <UploadCloud className="mx-auto text-[var(--text-secondary)]" />}
              <p className="font-mono text-xs uppercase tracking-widest text-[var(--text-secondary)] mt-3">{isDragActive ? "Drop them here" : "Drag photos / PDFs or click to upload"}</p>
            </div>

            {files.length > 0 && (
              <div className="grid grid-cols-4 gap-2" data-testid="bid-file-list">
                {files.map((f, i) => (
                  <div key={i} className="relative group aspect-square bg-[#050505] border border-[var(--border-dim)] overflow-hidden">
                    {f.preview ? <img src={f.preview} alt={f.name} className="w-full h-full object-cover" /> : <div className="flex items-center justify-center h-full font-mono text-[10px] p-1 text-center text-[var(--text-secondary)]">{f.name}</div>}
                    <button type="button" onClick={() => removeFile(i)} className="absolute top-1 right-1 bg-black/70 p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}

            <button type="submit" data-testid="bid-submit" disabled={submitting} className="w-full btn-wipe bg-[var(--accent)] text-white hover:text-white py-4 font-mono text-sm uppercase tracking-widest font-bold disabled:opacity-50 flex items-center justify-center gap-2">
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Send my bid request"}
            </button>
          </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
